// [PURPOSE]: "Want a quick tour?" mini-dialog shown on first tab visit
// [WHY]: Non-intrusive prompt that lets users opt-in to tab-specific tutorials
// [INTEGRATION]: Driven by TutorialContext pendingPrompt state
// [CREATED]: February 28, 2026 — Contextual Tutorial System redesign

import React from 'react';
import { useTutorial } from '../../contexts/TutorialContext';
import { TUTORIAL_MODULES } from '../../data/tutorialModules';
import { useModalEscape } from '../../hooks/useModalEscape';
import { Sparkles, X } from 'lucide-react';

// ========================================
// TUTORIAL PROMPT COMPONENT
// ========================================

const TutorialPrompt = () => {
  const { pendingPrompt, acceptPrompt, declinePrompt } = useTutorial();

  // [GATE-A11Y 2026-07-13]: Escape dismisses the prompt (declines it), matching
  // the Rule-19 keyboard-dismissal contract every other first-party modal honors.
  // Called before the early return so the hook order stays stable.
  useModalEscape(!!pendingPrompt, declinePrompt);

  if (!pendingPrompt) return null;

  const module = TUTORIAL_MODULES[pendingPrompt];
  if (!module) return null;

  const promptMessage = module.promptMessage || `Want a quick tour of ${module.title}?`;

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[140] animate-slide-up">
      <div className="bg-gray-800 border border-purple-500/60 rounded-xl shadow-2xl shadow-purple-900/30 px-5 py-3.5 flex items-center gap-4 max-w-md">
        {/* Icon */}
        <div className="flex-shrink-0 w-9 h-9 bg-purple-600/30 rounded-lg flex items-center justify-center border border-purple-500/40">
          <Sparkles size={18} className="text-purple-300" />
        </div>

        {/* Message */}
        <p className="text-gray-200 text-sm flex-1">
          {promptMessage}
        </p>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={acceptPrompt}
            className="px-3 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-medium rounded-lg transition-all shadow-lg shadow-purple-900/30"
          >
            Sure!
          </button>
          <button
            onClick={declinePrompt}
            className="text-gray-400 hover:text-gray-200 transition-colors p-1"
            title="No thanks"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Slide-up animation */}
      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TutorialPrompt;
